import React, { useState, useMemo } from 'react';
import { FaStar, FaRupeeSign } from 'react-icons/fa';

// Helper to format numbers as Indian Rupee
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value);
};

function OtherGoalCalculator() {
  // --- Input States ---
  const [targetAmount, setTargetAmount] = useState(2500000); // 25 Lakhs
  const [yearsToInvest, setYearsToInvest] = useState(10);
  const [expectedReturn, setExpectedReturn] = useState(12); // Investment return
  
  // --- Calculation Logic ---
  const results = useMemo(() => {
    // 3. Monthly SIP Required to reach the target
    const i = expectedReturn / 100 / 12; // Monthly return rate
    const N = yearsToInvest * 12; // Number of months to invest
    
    // If years are 0 or less, SIP is not applicable
    if (N <= 0) {
      return {
        monthlySip: 0,
        totalInvested: 0,
        totalGains: 0,
      };
    }

    // Future Value of SIP formula, solved for Monthly Payment (SIP)
    const monthlySip = (targetAmount * i) / (((1 + i) ** N - 1) * (1 + i));
    
    const totalInvested = monthlySip * N;
    const totalGains = targetAmount - totalInvested;

    return {
      monthlySip: Math.round(monthlySip),
      totalInvested: Math.round(totalInvested),
      totalGains: Math.round(totalGains),
    };
  }, [targetAmount, yearsToInvest, expectedReturn]);

  return (
    <div className="bg-gray-50 p-6 rounded-lg border">
      <div className="flex items-center mb-6">
        <FaStar className="h-8 w-8 text-blue-600 mr-3" />
        <h3 className="text-2xl font-bold text-gray-900">Your Other Goal Calculator</h3>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* --- Column 1: Inputs --- */}
        <div className="space-y-6">
          {/* Target Amount */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Target Amount</label>
              <span className="text-xl font-bold text-blue-600">{formatCurrency(targetAmount)}</span>
            </div>
            <input
              type="range" min="100000" max="20000000" step="100000"
              value={targetAmount}
              onChange={(e) => setTargetAmount(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Years to Invest */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Years to Invest</label>
              <span className="text-xl font-bold text-blue-600">{yearsToInvest} Years</span>
            </div>
            <input
              type="range" min="1" max="40" step="1"
              value={yearsToInvest}
              onChange={(e) => setYearsToInvest(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          
          {/* Expected Returns on Investment */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Expected Returns on Investment</label>
              <span className="text-xl font-bold text-blue-600">{expectedReturn}%</span>
            </div>
            <input
              type="range" min="5" max="20" step="0.5"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>

        {/* --- Column 2: Results --- */}
        <div className="bg-white rounded-xl p-6 border-2 border-blue-600 shadow-xl">
          <h3 className="text-xl font-bold text-blue-700 text-center">Your Goal Plan</h3>
          
          <div className="text-center my-6">
            <p className="text-lg text-gray-600">To reach your goal of</p>
            <p className="text-5xl font-extrabold text-gray-900 my-2">
              {formatCurrency(targetAmount)}
            </p>
            <p className="text-base text-gray-500">
              in {yearsToInvest} years, you need to invest:
            </p>
          </div>
          
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-lg font-semibold text-gray-800 text-center">Required Investment</h4>
            
            <p className="text-3xl font-bold text-blue-600 text-center my-3">
              {formatCurrency(results.monthlySip)}
            </p>
            <p className="text-base text-gray-600 text-center">
              per month.
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 mt-6 space-y-3">
            <div className="flex justify-between text-base">
              <span className="text-gray-600">Total Amount Invested</span>
              <span className="font-bold text-gray-800">{formatCurrency(results.totalInvested)}</span>
            </div>
            <div className="flex justify-between text-base">
              <span className="text-gray-600">Estimated Gains</span>
              <span className="font-bold text-green-600">{formatCurrency(results.totalGains)}</span>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
}

export default OtherGoalCalculator;